/*
  HUSTLESTREET — Collection page (PHASE 12)
  File: assets/hs-collection.js
  Loaded by: sections/hs-main-collection.liquid

  <hs-collection>
    - AJAX filtering + sorting through the Section Rendering API (no full page reload)
    - Keeps URL in sync (back/forward supported)
    - Filter drawer (mobile, or desktop "drawer" mode) with focus trap + Escape
    - "Load more" pagination (appends products)
    - Without JavaScript: filters/sort still work with the Apply buttons, pagination links work normally
*/
(function () {
  if (!('customElements' in window) || customElements.get('hs-collection')) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  class HsCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.defaultSort = this.dataset.defaultSort || '';
      this.form = this.querySelector('[data-hs-filter-form]');
      this.panel = this.querySelector('[data-hs-filter-panel]');
      this.sortSelect = this.querySelector('[data-hs-sort]');
      this.status = this.querySelector('[data-hs-collection-status]');

      this.onFormInput = this.onFormInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onSortChange = this.onSortChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);

      if (this.form) {
        this.form.addEventListener('input', this.onFormInput);
        this.form.addEventListener('submit', this.onSubmit);
      }
      if (this.sortSelect) this.sortSelect.addEventListener('change', this.onSortChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('input', this.onFormInput);
        this.form.removeEventListener('submit', this.onSubmit);
      }
      if (this.sortSelect) this.sortSelect.removeEventListener('change', this.onSortChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);
      window.clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      document.documentElement.classList.remove('hs-filter-is-open');
    }

    /* ---------- Query ---------- */

    buildQuery() {
      const params = new URLSearchParams();

      if (this.form) {
        new FormData(this.form).forEach((value, key) => {
          if (value !== '') params.append(key, value);
        });
      }

      if (this.sortSelect && !params.has('sort_by') && this.sortSelect.value) {
        params.set('sort_by', this.sortSelect.value);
      }

      return params.toString();
    }

    /* ---------- Events ---------- */

    onFormInput(event) {
      window.clearTimeout(this.timer);
      const delay = event.target && event.target.type === 'number' ? 700 : 250;
      this.timer = window.setTimeout(() => this.render(this.buildQuery()), delay);
    }

    onSubmit(event) {
      event.preventDefault();
      window.clearTimeout(this.timer);
      this.render(this.buildQuery());
    }

    onSortChange() {
      window.clearTimeout(this.timer);
      this.render(this.buildQuery());
    }

    onClick(event) {
      const opener = event.target.closest('[data-hs-filter-open]');
      if (opener) {
        event.preventDefault();
        this.openFilters(opener);
        return;
      }

      const closer = event.target.closest('[data-hs-filter-close]');
      if (closer) {
        event.preventDefault();
        this.closeFilters();
        return;
      }

      const more = event.target.closest('[data-hs-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      const link = event.target.closest('a[data-hs-filter-link]');
      if (link) {
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.render(url.searchParams.toString());
      }
    }

    onPopState() {
      this.render(window.location.search.replace(/^\?/, ''), false);
    }

    onKeydown(event) {
      if (!this.classList.contains('is-filter-open') || !this.isModal()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeFilters();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- Filter drawer ---------- */

    isModal() {
      return !!this.panel && window.getComputedStyle(this.panel).position === 'fixed';
    }

    openFilters(trigger) {
      if (!this.panel) return;

      this.lastFocus = trigger || document.activeElement;
      this.classList.add('is-filter-open');
      this.querySelectorAll('[data-hs-filter-open]').forEach((btn) => btn.setAttribute('aria-expanded', 'true'));

      if (this.isModal()) {
        document.documentElement.classList.add('hs-filter-is-open');
        window.requestAnimationFrame(() => {
          const close = this.panel.querySelector('[data-hs-filter-close]');
          (close || this.panel).focus();
        });
      }
    }

    closeFilters() {
      if (!this.classList.contains('is-filter-open')) return;

      this.classList.remove('is-filter-open');
      this.querySelectorAll('[data-hs-filter-open]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      document.documentElement.classList.remove('hs-filter-is-open');

      if (this.lastFocus && this.lastFocus.isConnected && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    /* ---------- Rendering ---------- */

    async render(query, push = true) {
      if (!this.sectionId) return;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const params = new URLSearchParams(query);
      params.set('section_id', this.sectionId);

      const pageUrl = window.location.pathname + (query ? '?' + query : '');
      this.classList.add('is-loading');

      try {
        const response = await fetch(window.location.pathname + '?' + params.toString(), {
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error('Filter request failed');

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        this.swap(doc);

        if (push) window.history.pushState({ hsCollection: true }, '', pageUrl);

        if (this.sortSelect) {
          const sort = new URLSearchParams(query).get('sort_by');
          this.sortSelect.value = sort || this.defaultSort;
        }

        this.classList.remove('is-loading');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.classList.remove('is-loading');
        window.location.href = pageUrl;
      }
    }

    swap(doc) {
      ['[data-hs-collection-results]', '[data-hs-collection-active]'].forEach((selector) => {
        const fresh = doc.querySelector(selector);
        const current = this.querySelector(selector);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      });

      const freshBody = doc.querySelector('[data-hs-filters-body]');
      const body = this.querySelector('[data-hs-filters-body]');

      if (freshBody && body) {
        const states = {};
        body.querySelectorAll('details[data-filter-id]').forEach((details) => {
          states[details.dataset.filterId] = details.open;
        });

        const active = document.activeElement;
        const focusedId = active && body.contains(active) ? active.id : null;

        body.innerHTML = freshBody.innerHTML;

        body.querySelectorAll('details[data-filter-id]').forEach((details) => {
          const id = details.dataset.filterId;
          if (Object.prototype.hasOwnProperty.call(states, id)) details.open = states[id];
        });

        if (focusedId) {
          const target = document.getElementById(focusedId);
          if (target) target.focus({ preventScroll: true });
        }
      }

      const meta = this.querySelector('[data-hs-meta]');
      if (meta) {
        const countText = meta.dataset.countText || '';
        const activeCount = parseInt(meta.dataset.active, 10) || 0;

        this.querySelectorAll('[data-hs-collection-count]').forEach((el) => {
          el.textContent = countText;
        });

        this.querySelectorAll('[data-hs-filter-count]').forEach((el) => {
          el.textContent = activeCount > 0 ? '(' + activeCount + ')' : '';
        });

        this.announce(countText);
      }
    }

    async loadMore(link) {
      if (this.loadingMore) return;
      this.loadingMore = true;
      link.classList.add('is-loading');
      link.setAttribute('aria-busy', 'true');

      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error('Load more failed');

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = this.querySelector('[data-hs-collection-grid]');
        const freshItems = Array.from(doc.querySelectorAll('[data-hs-collection-grid] > li'));

        if (grid) freshItems.forEach((item) => grid.appendChild(item));

        const freshWrap = doc.querySelector('[data-hs-more-wrap]');
        const wrap = this.querySelector('[data-hs-more-wrap]');
        if (wrap) {
          if (freshWrap) {
            wrap.replaceWith(freshWrap);
          } else {
            wrap.remove();
          }
        }

        const firstLink = freshItems.length ? freshItems[0].querySelector('a[href]') : null;
        if (firstLink) firstLink.focus({ preventScroll: true });

        this.announce(freshItems.length + ' ' + (this.dataset.moreText || 'more products loaded'));
      } catch (error) {
        window.location.href = link.href;
      } finally {
        this.loadingMore = false;
        if (link.isConnected) {
          link.classList.remove('is-loading');
          link.setAttribute('aria-busy', 'false');
        }
      }
    }

    announce(text) {
      if (!this.status || !text) return;
      this.status.textContent = '';
      window.setTimeout(() => {
        this.status.textContent = text;
      }, 60);
    }
  }

  customElements.define('hs-collection', HsCollection);
})();